import {
  CalendarCell as AriaCalendarCell,
  RangeCalendar as AriaRangeCalendar,
  type RangeCalendarProps as AriaRangeCalendarProps,
  type CalendarCellProps,
  CalendarGridBody,
  type DateValue,
  Text,
} from "react-aria-components";

import { focusStyles } from "@beweegbaas/ui/lib/focus-styles";
import { cn } from "@beweegbaas/ui/lib/utils";
import { cva } from "class-variance-authority";
import { CalendarGrid, CalendarGridHeader, CalendarHeaderWithDropdowns } from "./calendar";

export interface RangeCalendarProps<T extends DateValue> extends AriaRangeCalendarProps<T> {
  errorMessage?: string;
}

export const calendarCellStyles = cva(
  cn(
    "mx-auto flex size-9 items-center justify-center rounded-full text-neutral-900 forced-color-adjust-none dark:text-neutral-200",
    focusStyles,
  ),
  {
    variants: {
      selectionState: {
        none: "group-hover:bg-neutral-200 dark:group-hover:bg-neutral-700 group-pressed:bg-neutral-300 dark:group-pressed:bg-neutral-600",
        middle: [
          "group-hover:bg-blue-200 dark:group-hover:bg-blue-900 forced-colors:group-hover:bg-[Highlight]",
          "group-invalid:group-hover:bg-red-200 dark:group-invalid:group-hover:bg-red-900 forced-colors:group-invalid:group-hover:bg-[Mark]",
          "group-pressed:bg-blue-300 dark:group-pressed:bg-blue-800 forced-colors:group-pressed:bg-[Highlight] forced-colors:text-[HighlightText]",
          "group-invalid:group-pressed:bg-red-300 dark:group-invalid:group-pressed:bg-red-800 forced-colors:group-invalid:group-pressed:bg-[Mark]",
        ],
        cap: "bg-blue-600 group-invalid:bg-red-600 forced-colors:bg-[Highlight] forced-colors:group-invalid:bg-[Mark] text-white forced-colors:text-[HighlightText]",
      },
      isFocusVisible: {
        true: "focus-visible:ring-4 focus-visible:ring-blue-300",
      },
      isDisabled: {
        true: "text-neutral-300! dark:text-neutral-600 forced-colors:text-[GrayText]",
      },
    },
  },
);

const cellStyles = calendarCellStyles;

export function RangeCalendarCell({ className, ...props }: CalendarCellProps) {
  return (
    <AriaCalendarCell
      {...props}
      className={cn(
        // The range band spans the (fluid) cell; start/end and row edges blank their outer half,
        // and the circle layer rounds a row-edge band around the day. Separate vars avoid
        // variant-order conflicts.
        `group outside-month:text-neutral-300
        selected:[--band:var(--color-blue-100)] dark:selected:[--band:color-mix(in_oklab,var(--color-blue-700)_30%,transparent)]
        invalid:selected:[--band:var(--color-red-100)] dark:invalid:selected:[--band:color-mix(in_oklab,var(--color-red-700)_30%,transparent)]
        forced-colors:selected:[--band:Highlight] forced-colors:invalid:selected:[--band:Mark]
        selection-start:[--band-start:transparent] selection-end:[--band-end:transparent]
        [td:first-child_&]:[--band-start:transparent] [td:last-child_&]:[--band-end:transparent]
        bg-[radial-gradient(circle,var(--band,transparent)_1.125rem,transparent_calc(1.125rem+0.5px)),linear-gradient(to_right,var(--band-start,var(--band,transparent))_50%,var(--band-end,var(--band,transparent))_50%)]
        my-px h-9 w-full min-w-9 cursor-default
        text-sm
        outline outline-0
        [-webkit-tap-highlight-color:transparent]`,
        className,
      )}
    >
      {({
        formattedDate,
        isSelected,
        isSelectionStart,
        isSelectionEnd,
        isFocusVisible,
        isDisabled,
      }) => (
        <span
          className={cellStyles({
            selectionState:
              isSelected && (isSelectionStart || isSelectionEnd)
                ? "cap"
                : isSelected
                  ? "middle"
                  : "none",
            isDisabled,
            isFocusVisible,
          })}
        >
          {formattedDate}
        </span>
      )}
    </AriaCalendarCell>
  );
}

export function RangeCalendar<T extends DateValue>({
  errorMessage,
  children,
  className,
  ...props
}: RangeCalendarProps<T>) {
  const defaultChildren = (
    <>
      <CalendarHeaderWithDropdowns />
      <CalendarGrid className="w-full table-fixed border-spacing-0 [&_td]:px-0 [&_td]:py-px">
        <CalendarGridHeader />
        <CalendarGridBody>{(date) => <RangeCalendarCell date={date} />}</CalendarGridBody>
      </CalendarGrid>
      {errorMessage && (
        <Text slot="errorMessage" className="text-sm text-red-600">
          {errorMessage}
        </Text>
      )}
    </>
  );

  return (
    <AriaRangeCalendar
      {...props}
      className={cn(
        "@container font-sans",
        // Bleed half a column gap past each side so the outer day circles (and the arrows above
        // them) line up with the container edge: 15.75rem is seven 2.25rem minimum cells.
        children ? "max-w-full" : "-mx-[calc((100%-15.75rem)/12)]",
        className,
      )}
    >
      {children || defaultChildren}
    </AriaRangeCalendar>
  );
}
